// src/pages/Posts.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  collectionGroup,
  collection,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
  deleteDoc,
  updateDoc, // 🔹 NEW
} from "firebase/firestore";
import { db } from "../firebase";
import { getStorage, ref as sRef, deleteObject } from "firebase/storage";
import { FaExclamationCircle } from "react-icons/fa";
import {
  FiEye,
  FiTrash2,
  FiUsers,
  FiMusic,
  FiSearch,
  FiFilter,
  FiClock,
  FiImage,
  FiVideo,
  FiFileText,
  FiPlay,
} from "react-icons/fi";

/* Theme */
const COLORS = {
  text: "#0f172a",
  subtext: "#475569",
  border: "#e2e8f0",
  card: "#ffffff",
  primary: "#f20c53",
  secondary: "#fb6c33",
  warn: "#b91c1c",
  muted: "#94a3b8",
  page: "#f5f6f8",
};

export default function Posts() {
  const [tab, setTab] = useState("users"); // "users" | "artists"
  const [usersPosts, setUsersPosts] = useState([]);
  const [artistsPosts, setArtistsPosts] = useState([]);
  const [usersDupRemoved, setUsersDupRemoved] = useState(0);
  const [artistsDupRemoved, setArtistsDupRemoved] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // permissions
  const [canView, setCanView] = useState(true);
  const [canDelete, setCanDelete] = useState(false);

  // viewer modal
  const [viewer, setViewer] = useState({ open: false, url: "" });

  // deleting state per id
  const [deleting, setDeleting] = useState({}); // { [id]: true }

  // toast
  const [toast, setToast] = useState("");

  // UI controls
  const [search, setSearch] = useState("");
  const [mediaFilter, setMediaFilter] = useState("All"); // All | Images | Videos | PDFs | Other
  const [sortKey, setSortKey] = useState("Newest"); // Newest | Oldest

  // artist uid -> stageName map
  const [artistNameByUid, setArtistNameByUid] = useState({}); // { [uid]: stageName }

  // add shimmer keyframes once (safe on mount)
  useEffect(() => {
    if (typeof document !== "undefined" && !document.getElementById("posts-shimmer-style")) {
      const kf = document.createElement("style");
      kf.id = "posts-shimmer-style";
      kf.textContent = `
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `;
      document.head.appendChild(kf);
    }
  }, []);

  // live map of artists (uid => stageName)
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "artists"),
      (snap) => {
        const map = {};
        snap.forEach((doc) => {
          const a = doc.data() || {};
          const uid = a.uid || doc.id;
          const stageName =
            a.stageName ||
            a.stage_name ||
            a.artistName ||
            a.displayName ||
            a.name ||
            "—";
          if (uid) map[uid] = stageName;
        });
        setArtistNameByUid(map);
      },
      (e) => {
        console.warn("artists map load failed:", e?.message || e);
      }
    );
    return () => unsub();
  }, []);

  // stable error handler (fixes hooks/exhaustive-deps)
  const handleErr = useCallback((e, kind) => {
    console.error(e);
    let msg = `Failed to load ${kind} posts.`;
    if (e?.code === "permission-denied") {
      msg = "Permission denied by Firestore rules.";
    } else if (e?.code === "failed-precondition" && /index/i.test(e?.message || "")) {
      msg = "A Firestore composite index is required. Open the console link in the error log.";
    }
    setErr(msg);
    setLoading(false);
  }, []);

  useEffect(() => {
    const employee = JSON.parse(localStorage.getItem("employee"));
    const role = employee?.role;
    const perms = employee?.permissions || [];

    const allowedView = role === "super_admin" || perms.includes("view_posts");
    setCanView(allowedView);

    const allowedDelete = role === "super_admin" || perms.includes("delete_posts");
    setCanDelete(allowedDelete);

    if (!allowedView) return;

    // Streams across ANY parent …/posts/*
    const qUsers = query(
      collectionGroup(db, "posts"),
      where("ownerType", "==", "user"),
      orderBy("createdAt", "desc"),
      limit(100)
    );

    const qArtists = query(
      collectionGroup(db, "posts"),
      where("ownerType", "==", "artist"),
      orderBy("createdAt", "desc"),
      limit(100)
    );

    const unsubUsers = onSnapshot(
      qUsers,
      (snap) => {
        const list = snap.docs.map((d) => normalizePost(d, d.data()));
        const deduped = dedupePosts(list);
        setUsersPosts(deduped.rows);
        setUsersDupRemoved(deduped.removedCount);
        setLoading(false);
      },
      (e) => handleErr(e, "users")
    );

    const unsubArtists = onSnapshot(
      qArtists,
      (snap) => {
        const list = snap.docs.map((d) => normalizePost(d, d.data()));
        const deduped = dedupePosts(list);
        setArtistsPosts(deduped.rows);
        setArtistsDupRemoved(deduped.removedCount);
        setLoading(false);
      },
      (e) => handleErr(e, "artists")
    );

    return () => {
      unsubUsers();
      unsubArtists();
    };
  }, [handleErr]);

  const activePosts = useMemo(
    () => (tab === "users" ? usersPosts : artistsPosts),
    [tab, usersPosts, artistsPosts]
  );

  // stable display name resolver (fixes hooks/exhaustive-deps when used in useMemo)
  const displayNameOf = useCallback(
    (p) =>
      p.ownerType === "artist"
        ? artistNameByUid[p.ownerId] || p.author || "—"
        : p.author || "—",
    [artistNameByUid]
  );

  const filteredSorted = useMemo(() => {
    let arr = [...activePosts];

    const q = search.trim().toLowerCase();
    if (q) {
      arr = arr.filter((p) => {
        const name = displayNameOf(p);
        const hay = `${name || ""} ${p.caption || ""}`.toLowerCase();
        return hay.includes(q);
      });
    }

    if (mediaFilter !== "All") {
      arr = arr.filter((p) => mediaType(p.media) === mediaFilter);
    }

    if (sortKey === "Newest") {
      arr.sort((a, b) => dateVal(b.createdAt) - dateVal(a.createdAt));
    } else if (sortKey === "Oldest") {
      arr.sort((a, b) => dateVal(a.createdAt) - dateVal(b.createdAt));
    }

    return arr;
  }, [activePosts, search, mediaFilter, sortKey, displayNameOf]);

  async function handleDelete(post) {
    if (!canDelete || !post) return;
    const opKey = post.docPath || post.id;
    try {
      setDeleting((m) => ({ ...m, [opKey]: true }));

      // 1) Try to delete media from Storage (if we can resolve a path)
      await deleteMediaFromStorage(post.media || post.storagePath);

      // 2) Delete the Firestore document
      await deleteDoc(post.ref); // exact path

      setToast("Post deleted.");
      setTimeout(() => setToast(""), 2000);
    } catch (e) {
      console.error(e);
      setToast("Delete failed.");
      setTimeout(() => setToast(""), 2000);
    } finally {
      setDeleting((m) => ({ ...m, [opKey]: false }));
    }
  }

  // 🔹 NEW: toggle isFeatured on a post
  async function handleToggleFeatured(post, isFeatured) {
    if (!post) return;
    if (!canDelete) {
      setToast("You do not have permission to change featured status.");
      setTimeout(() => setToast(""), 2000);
      return;
    }
    try {
      await updateDoc(post.ref, { isFeatured: !!isFeatured });
      setToast(`Featured set to ${isFeatured ? "true" : "false"}.`);
      setTimeout(() => setToast(""), 2000);
    } catch (e) {
      console.error(e);
      setToast("Failed to update featured status.");
      setTimeout(() => setToast(""), 2000);
    }
  }

  function openViewer(url) {
    if (!url) return;
    setViewer({ open: true, url });
  }
  function closeViewer() {
    setViewer((v) => ({ ...v, open: false }));
  }

  if (!canView) {
    return (
      <div style={deniedWrap}>
        <div style={deniedCard}>
          <FaExclamationCircle size={36} color="#e53935" />
          <h3 style={{ margin: "1rem 0 0" }}>Access Denied</h3>
          <p style={{ marginTop: 8, color: COLORS.subtext }}>
            You do not have permission to view user/artist posts.
          </p>
        </div>
      </div>
    );
  }

  const totals = {
    users: usersPosts.length,
    artists: artistsPosts.length,
    all: usersPosts.length + artistsPosts.length,
    removed: usersDupRemoved + artistsDupRemoved,
  };

  return (
    <div
      className="aa-pro-page"
      style={{
        padding: 16,
        fontFamily: "Inter, system-ui, sans-serif",
        background: COLORS.page,
      }}
    >
      {/* Hero */}
      <div className="aa-pro-hero" style={hero}>
        <div style={heroLeft}>
          <div className="aa-pro-hero-icon" style={heroIcon}>
            <FiUsers />
          </div>
          <div>
            <h2 className="aa-pro-title" style={heroTitle}>Posts</h2>
            <p className="aa-pro-subtitle" style={heroSub}>Review user and artist posts with fast filters and media previews.</p>
          </div>
        </div>
        <div className="aa-pro-metrics" style={heroStats}>
          <Stat label="Total" value={totals.all} />
          <Stat label="Users" value={totals.users} />
          <Stat label="Artists" value={totals.artists} />
        </div>
      </div>

      {/* Tabs */}
      <div className="aa-pro-segment" style={{ display: "flex", gap: 8, marginTop: 6, marginBottom: 12 }}>
        <button
          onClick={() => setTab("users")}
          style={{
            ...tabBtn,
            background:
              tab === "users"
                ? `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`
                : "#fff",
            color: tab === "users" ? "#fff" : COLORS.text,
          }}
          title="Users Posts"
        >
          <FiUsers />
          <span style={{ fontWeight: 800 }}>Users Posts</span>
          <span style={pillCount}>{totals.users}</span>
        </button>
        <button
          onClick={() => setTab("artists")}
          style={{
            ...tabBtn,
            background:
              tab === "artists"
                ? `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`
                : "#fff",
            color: tab === "artists" ? "#fff" : COLORS.text,
          }}
          title="Artists Posts"
        >
          <FiMusic />
          <span style={{ fontWeight: 800 }}>Artists Posts</span>
          <span style={pillCount}>{totals.artists}</span>
        </button>
      </div>

      {/* Controls */}
      <div className="aa-pro-toolbar" style={controlsRow}>
        <div style={searchBox}>
          <FiSearch style={{ opacity: 0.7 }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by author or caption…"
            style={searchInput}
          />
        </div>

        <div style={controlsRight}>
          <div style={controlGroup}>
            <FiFilter />
            <select
              value={mediaFilter}
              onChange={(e) => setMediaFilter(e.target.value)}
              style={select}
            >
              <option>All</option>
              <option>Images</option>
              <option>Videos</option>
              <option>PDFs</option>
              <option>Other</option>
            </select>
          </div>
          <div style={controlGroup}>
            <FiClock />
            <select
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value)}
              style={select}
            >
              <option>Newest</option>
              <option>Oldest</option>
            </select>
          </div>
        </div>
      </div>

      {totals.removed > 0 && (
        <div style={dedupeNote}>
          Hidden duplicate posts: {totals.removed} (Users: {usersDupRemoved}, Artists: {artistsDupRemoved})
        </div>
      )}

      {loading && (
        <div style={skeletonGrid}>
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} style={skeletonCard} />
          ))}
        </div>
      )}
      {err && <div style={{ color: COLORS.warn, marginTop: 8 }}>{err}</div>}

      {/* Grid */}
      {!loading && !err && (
        <div
          className="aa-pro-gallery-grid"
          style={{
            display: "grid",
            gap: "1.2rem",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          }}
        >
          {filteredSorted.map((p) => (
            <PostCard
              key={p.docPath || `${p.ownerType || "post"}:${p.id}`}
              post={p}
              displayName={displayNameOf(p)}
              canDelete={canDelete}
              canFeature={canDelete} // 🔹 use same permission for now
              deleting={!!deleting[p.docPath || p.id]}
              onView={() => openViewer(p.media)}
              onDelete={() => handleDelete(p)}
              onToggleFeatured={(val) => handleToggleFeatured(p, val)} // 🔹 NEW
            />
          ))}

          {filteredSorted.length === 0 && (
            <div
              style={{
                border: `1px dashed ${COLORS.border}`,
                borderRadius: 12,
                padding: 16,
                color: COLORS.subtext,
                background: "#fff",
              }}
            >
              No posts match your filters.
            </div>
          )}
        </div>
      )}

      {/* Viewer modal */}
      {viewer.open && (
        <Modal onClose={closeViewer} title="Preview">
          <MediaViewer url={viewer.url} />
        </Modal>
      )}

      {/* Toast */}
      {toast && <div style={toastBox}>{toast}</div>}
    </div>
  );
}

/* ───────── Components ───────── */

function PostCard({
  post,
  displayName,
  canDelete,
  canFeature,
  deleting,
  onView,
  onDelete,
  onToggleFeatured,
}) {
  const { caption, media, createdAt, isFeatured } = post;
  const mType = mediaType(media);
  const badge = mediaBadge(mType);

  return (
    <div
      style={{
        backgroundColor: COLORS.card,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 12,
        padding: 12,
        boxShadow: "0 6px 14px rgba(0,0,0,.06)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Media (actions overlay INSIDE, on top) */}
      {media ? (
        <div
          onClick={onView}
          title="View"
          style={{
            border: `1px solid ${COLORS.border}`,
            borderRadius: 10,
            overflow: "hidden",
            cursor: "pointer",
            position: "relative",
          }}
        >
          <MediaThumb url={media} />

          {/* Overlay actions */}
          <div style={overlayActions} onClick={(e) => e.stopPropagation()}>
            <button onClick={onView} title="View" style={iconBtn}>
              <FiEye />
            </button>
            {canDelete && (
              <button
                onClick={onDelete}
                title="Delete"
                style={{
                  ...iconBtn,
                  color: deleting ? COLORS.muted : COLORS.warn,
                }}
                disabled={deleting}
              >
                <FiTrash2 />
              </button>
            )}
          </div>

          {/* Type badge */}
          <div style={mediaBadgeStyle}>
            {badge.icon} <span>{badge.label}</span>
          </div>

          {/* Play overlay for video */}
          {mType === "Videos" && (
            <div style={playOverlay}>
              <FiPlay />
            </div>
          )}
        </div>
      ) : (
        <div
          style={{
            border: `1px dashed ${COLORS.border}`,
            borderRadius: 10,
            padding: 12,
            color: COLORS.subtext,
            textAlign: "center",
          }}
        >
          No media
        </div>
      )}

      {/* Author */}
      <p style={{ margin: "10px 0 4px", fontWeight: 800, color: COLORS.text }}>
        {displayName}
      </p>
      {/* Caption */}
      {caption && (
        <p style={{ margin: 0, whiteSpace: "pre-wrap", color: COLORS.text }}>
          {caption}
        </p>
      )}

      {/* 🔹 Featured toggle */}
      <div
        style={{
          marginTop: 8,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <span
          style={{
            fontSize: 12,
            color: COLORS.subtext,
            fontWeight: 700,
          }}
        >
          Featured
        </span>
        <select
          value={isFeatured ? "true" : "false"}
          onChange={(e) => onToggleFeatured(e.target.value === "true")}
          style={inlineSelect}
          disabled={!canFeature}
          title={canFeature ? "Mark as featured" : "No permission"}
        >
          <option value="false">False</option>
          <option value="true">True</option>
        </select>
      </div>

      {/* Meta */}
      <p style={{ fontSize: 12, color: COLORS.subtext, marginTop: 8 }}>
        Posted on {formatDate(createdAt)}
      </p>
    </div>
  );
}

function MediaThumb({ url }) {
  const isImg = /\.(png|jpe?g|webp|gif)(\?|$)/i.test(url);
  const isVideo = /\.(mp4|webm|ogg)(\?|$)/i.test(url);
  return (
    <>
      {isImg ? (
        <img
          src={url}
          alt="media"
          style={{
            width: "100%",
            height: 160,
            objectFit: "cover",
            display: "block",
            position: "relative",
            zIndex: 1,
          }}
        />
      ) : isVideo ? (
        <video
          src={url}
          style={{
            width: "100%",
            height: 160,
            objectFit: "cover",
            display: "block",
            position: "relative",
            zIndex: 1,
          }}
          muted
        />
      ) : (
        <div
          style={{
            padding: 12,
            color: COLORS.subtext,
            fontSize: 13,
            textAlign: "center",
          }}
        >
          Tap the eye icon to open file.
        </div>
      )}
    </>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div style={modalBackdrop}>
      <div style={modalCard}>
        <div style={modalHeader}>
          <div style={{ color: "#fff", fontWeight: 900 }}>{title}</div>
          <button onClick={onClose} style={modalCloseBtn} aria-label="Close">
            ✕
          </button>
        </div>
        <div style={{ padding: 12 }}>{children}</div>
      </div>
    </div>
  );
}

function MediaViewer({ url }) {
  const isPdf = /\.pdf(\?|$)/i.test(url);
  const isImg = /\.(png|jpe?g|webp|gif)(\?|$)/i.test(url);
  const isVideo = /\.(mp4|webm|ogg)(\?|$)/i.test(url);

  if (isPdf) {
    return (
      <iframe
        title="PDF"
        src={url}
        style={{
          width: "100%",
          height: 520,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 8,
        }}
      />
    );
  }
  if (isImg) {
    return (
      <img
        alt="document"
        src={url}
        style={{
          width: "100%",
          maxHeight: 520,
          objectFit: "contain",
          border: `1px solid ${COLORS.border}`,
          borderRadius: 8,
        }}
      />
    );
  }
  if (isVideo) {
    return (
      <video
        controls
        src={url}
        style={{
          width: "100%",
          maxHeight: 520,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 8,
        }}
      />
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" style={linkBtn}>
      Open file
    </a>
  );
}

function Stat({ label, value }) {
  return (
    <div
      className="aa-pro-metric"
      style={{
        background: "#fff",
        border: `1px solid ${COLORS.border}`,
        borderRadius: 10,
        padding: "8px 12px",
        minWidth: 90,
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: 12, color: COLORS.subtext }}>{label}</div>
      <div style={{ fontWeight: 900, color: COLORS.text }}>{value}</div>
    </div>
  );
}

/* ───────── Helpers ───────── */

function normalizePost(docSnap, x = {}) {
  const id = docSnap.id;
  const ref = docSnap.ref;

  const author =
    x.author ||
    x.authorName ||
    x.userName ||
    x.artistName ||
    x.name ||
    "—";

  const caption = x.caption || x.text || x.message || "";

  const media =
    x.media ||
    x.mediaUrl ||
    x.imageUrl ||
    x.videoUrl ||
    x.fileUrl ||
    "";

  return {
    id,
    ref,
    docPath: ref?.path || "",
    author,
    caption,
    media,
    createdAt: x.createdAt || null,
    ownerId: x.ownerId || null, // used to look up stageName
    ownerType: x.ownerType || null, // "artist" or "user"
    postId: x.postId || x.post_id || null,
    sourcePostId: x.sourcePostId || x.originalPostId || null,
    storagePath: x.storagePath || null,
    isFeatured: !!x.isFeatured, // 🔹 NEW
  };
}

function dedupePosts(rows = []) {
  const seen = new Set();
  const deduped = [];

  for (const row of rows) {
    const key = dedupePostKey(row);
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(row);
  }

  return { rows: deduped, removedCount: Math.max(0, rows.length - deduped.length) };
}

function dedupePostKey(row = {}) {
  const stamp = dateVal(row.createdAt);
  const owner = String(row.ownerType || "unknown");
  const ownerId = String(row.ownerId || row.author || "");
  const caption = String(row.caption || "").trim().toLowerCase();
  const media = normalizeMediaUrl(row.media || "");
  const explicit = String(row.postId || row.sourcePostId || "").trim();
  if (explicit) return `id:${owner}:${ownerId}:${explicit}`;
  return `${owner}|${ownerId}|${caption}|${media}|${stamp}`;
}

function normalizeMediaUrl(url = "") {
  const trimmed = String(url || "").trim().toLowerCase();
  if (!trimmed) return "";
  return trimmed.split("?")[0];
}

function mediaType(url = "") {
  if (!url) return "Other";
  if (/\.pdf(\?|$)/i.test(url)) return "PDFs";
  if (/\.(png|jpe?g|webp|gif)(\?|$)/i.test(url)) return "Images";
  if (/\.(mp4|webm|ogg)(\?|$)/i.test(url)) return "Videos";
  return "Other";
}

function mediaBadge(type) {
  if (type === "Images") return { icon: <FiImage />, label: "Image" };
  if (type === "Videos") return { icon: <FiVideo />, label: "Video" };
  if (type === "PDFs") return { icon: <FiFileText />, label: "PDF" };
  return { icon: <FiFileText />, label: "File" };
}

function dateVal(ts) {
  try {
    if (!ts) return 0;
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.getTime() || 0;
  } catch {
    return 0;
  }
}

function formatDate(ts) {
  try {
    if (!ts) return "—";
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString();
  } catch {
    return "—";
  }
}

/** Resolve a Firebase Storage path from either a stored path or a downloadURL */
function toStoragePath(urlOrPath) {
  if (!urlOrPath) return null;
  // if they already stored a gs path or a plain path
  if (/^gs:\/\//i.test(urlOrPath)) return urlOrPath;
  if (!/^https?:\/\//i.test(urlOrPath)) return urlOrPath.replace(/^\/+/, "");

  // try to extract ".../o/<ENCODED_PATH>?..."
  const m = urlOrPath.match(/\/o\/(.+?)\?/);
  if (m && m[1]) return decodeURIComponent(m[1]);
  return null;
}

async function deleteMediaFromStorage(media) {
  try {
    const path = toStoragePath(media);
    if (!path) return; // nothing to delete
    const storage = getStorage();
    await deleteObject(sRef(storage, path));
  } catch (e) {
    // Non-fatal: if file is missing or permission issue, skip
    console.warn("Storage delete skipped/failed:", e?.message || e);
  }
}

/* ───────── Styles ───────── */
const hero = {
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
  color: "#fff",
  padding: "14px 16px",
  borderRadius: 14,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  boxShadow: "0 14px 26px rgba(0,0,0,0.12)",
  marginBottom: 14,
};
const heroLeft = { display: "flex", alignItems: "center", gap: 12 };
const heroIcon = {
  width: 42,
  height: 42,
  borderRadius: 10,
  display: "grid",
  placeItems: "center",
  background: "rgba(255,255,255,0.12)",
  border: "1px solid rgba(255,255,255,0.35)",
};
const heroTitle = { margin: 0, fontSize: 20, fontWeight: 900, lineHeight: 1.1 };
const heroSub = { margin: "4px 0 0", opacity: 0.92, fontSize: 13 };
const heroStats = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  flexWrap: "wrap",
};

const controlsRow = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
  alignItems: "center",
  marginBottom: 12,
};
const dedupeNote = {
  border: `1px solid ${COLORS.border}`,
  background: "#fff7ed",
  color: COLORS.text,
  borderRadius: 10,
  padding: "8px 12px",
  marginBottom: 12,
  fontSize: 13,
  fontWeight: 700,
};
const searchBox = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  padding: "10px 12px",
  background: "#fff",
  minWidth: 260,
  flex: 1,
};
const searchInput = {
  border: "none",
  outline: "none",
  flex: 1,
  fontSize: 14,
};
const controlsRight = { display: "flex", gap: 10, flexWrap: "wrap" };
const controlGroup = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  padding: "8px 10px",
  background: "#fff",
};
const select = {
  border: "none",
  outline: "none",
  background: "transparent",
  fontSize: 14,
  paddingRight: 4,
  cursor: "pointer",
};

const tabBtn = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: 999,
  padding: "8px 12px",
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  cursor: "pointer",
  boxShadow: "0 4px 10px rgba(0,0,0,.06)",
};
const pillCount = {
  marginLeft: 6,
  padding: "0 8px",
  height: 22,
  lineHeight: "22px",
  borderRadius: 999,
  background: "#fff",
  color: COLORS.text,
  border: `1px solid ${COLORS.border}`,
  fontSize: 12,
  fontWeight: 800,
};

const iconBtn = {
  width: 34,
  height: 34,
  display: "grid",
  placeItems: "center",
  borderRadius: 8,
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  color: COLORS.text,
  cursor: "pointer",
};

const overlayActions = {
  position: "absolute",
  top: 8,
  right: 8,
  display: "flex",
  gap: 6,
  zIndex: 5,
  pointerEvents: "auto",
};

const mediaBadgeStyle = {
  position: "absolute",
  top: 8,
  left: 8,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  background: "rgba(255,255,255,0.92)",
  color: COLORS.text,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 999,
  padding: "4px 10px",
  fontWeight: 800,
  fontSize: 12,
  zIndex: 4,
};

const playOverlay = {
  position: "absolute",
  inset: 0,
  display: "grid",
  placeItems: "center",
  color: "#fff",
  fontSize: 28,
  background: "linear-gradient(0deg, rgba(0,0,0,0.35), rgba(0,0,0,0.2))",
  pointerEvents: "none",
  zIndex: 2,
};

const inlineSelect = {
  padding: "4px 8px",
  borderRadius: 8,
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  fontSize: 12,
  cursor: "pointer",
};

const linkBtn = {
  border: "none",
  background: "transparent",
  color: COLORS.primary,
  fontWeight: 800,
  cursor: "pointer",
  textDecoration: "none",
};

const modalBackdrop = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,.45)",
  display: "grid",
  placeItems: "center",
  zIndex: 60,
  padding: 16,
};
const modalCard = {
  width: "100%",
  maxWidth: 820,
  background: "#fff",
  border: `1px solid ${COLORS.border}`,
  borderRadius: 12,
  overflow: "hidden",
  boxShadow: "0 10px 30px rgba(0,0,0,.25)",
};
const modalHeader = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: 12,
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
  color: "#fff",
  fontWeight: 900,
};
const modalCloseBtn = {
  width: 34,
  height: 34,
  display: "grid",
  placeItems: "center",
  borderRadius: 8,
  border: "1px solid rgba(255,255,255,0.45)",
  background: "transparent",
  color: "#fff",
  cursor: "pointer",
};

const deniedWrap = {
  position: "fixed",
  inset: 0,
  backdropFilter: "blur(5px)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 999,
};
const deniedCard = {
  backgroundColor: "#fff",
  padding: "2rem",
  borderRadius: "10px",
  textAlign: "center",
  boxShadow: "0 0 20px rgba(0,0,0,0.15)",
  maxWidth: "400px",
};

const skeletonGrid = {
  display: "grid",
  gap: "1.2rem",
  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
};
const skeletonCard = {
  height: 248,
  background: `linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 37%, #f3f4f6 63%)`,
  backgroundSize: "400% 100%",
  borderRadius: 14,
  animation: "shimmer 1.6s infinite",
};

const toastBox = {
  position: "fixed",
  bottom: 16,
  right: 16,
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
  border: "none",
  color: "#fff",
  borderRadius: 10,
  padding: "10px 12px",
  boxShadow: "0 16px 26px rgba(0,0,0,.2)",
  fontWeight: 800,
  zIndex: 60,
};
